import { useState, useEffect, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, BookOpen, Eye, EyeOff, Star, X } from 'lucide-react';
import { Course, Lesson } from '../../types';
import { adminCoursesService } from '../../services/courses';
import Card, { CardBody } from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Modal from '../../components/common/Modal';
import ConfirmModal from '../../components/common/ConfirmModal';
import AccessExpiryConfig from '../../components/common/AccessExpiryConfig';
import { toEmbedUrl, validateVideoUrl } from '../../utils/videoUrl';
import { COMMON_TIMEZONES, timezoneLabel } from '../../utils/timezones';
import ImageEditor from '../../components/common/ImageEditor';
import { SUPPORTED_CURRENCIES } from '../../utils/currency';

// Lazy so the heavy TipTap editor bundle is never on the static path of public pages.
const RichTextEditor = lazy(() => import('../../components/common/RichTextEditor'));
import { useToast } from '../../hooks/useToast';

const BADGE_KEYS = ['popular', 'new', 'advanced', 'professional', 'specialized', 'inDemand'] as const;
const COLOR_KEYS = [
  { value: 'amber', labelKey: 'courses.colorAmber', className: 'bg-amber-500' },
  { value: 'blue', labelKey: 'courses.colorBlue', className: 'bg-blue-500' },
  { value: 'green', labelKey: 'courses.colorGreen', className: 'bg-green-500' },
  { value: 'purple', labelKey: 'courses.colorPurple', className: 'bg-purple-500' },
  { value: 'red', labelKey: 'courses.colorRed', className: 'bg-red-500' },
  { value: 'cyan', labelKey: 'courses.colorCyan', className: 'bg-cyan-500' },
  { value: 'pink', labelKey: 'courses.colorPink', className: 'bg-pink-500' },
];

export default function ManageCourses() {
  const { t } = useTranslation('admin');
  const { success, error: showError } = useToast();
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLessonModalOpen, setIsLessonModalOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant?: 'danger' | 'warning' | 'info';
  } | null>(null);

  const [courseForm, setCourseForm] = useState({
    title: '',
    slug: '',
    description: '',
    thumbnail: '',
    duration: '',
    price: '',
    currency: 'USD',
    category: '',
    startDate: '',
    startDateTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    accessDurationDays: 0,
    accessExpiresAt: '',
    status: 'draft' as 'draft' | 'published',
    // Featured fields
    isFeatured: false,
    featuredOrder: '',
    featuredBadge: '',
    featuredColor: 'amber',
    originalPrice: '',
    features: [] as string[],
  });

  const [newFeature, setNewFeature] = useState('');
  const [videoUrlError, setVideoUrlError] = useState('');

  const [lessonForm, setLessonForm] = useState({
    title: '',
    content: '',
    videoUrl: '',
    duration: '',
    orderNum: 0,
  });

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const data = await adminCoursesService.getAll();
      setCourses(data);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLessons = async (courseId: string) => {
    try {
      const data = await adminCoursesService.getLessons(courseId);
      setLessons(data);
    } catch (error) {
      console.error('Failed to fetch lessons:', error);
    }
  };

  const handleEditCourse = (course: Course) => {
    setSelectedCourse(course);
    setCourseForm({
      title: course.title,
      slug: course.slug,
      description: course.description,
      thumbnail: course.thumbnail || '',
      duration: course.duration,
      price: course.price.toString(),
      currency: course.currency || 'USD',
      category: course.category,
      startDate: course.startDate || '',
      startDateTimezone: course.startDateTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      accessDurationDays: course.accessDurationDays || 0,
      accessExpiresAt: course.accessExpiresAt || '',
      status: course.status === 'published' ? 'published' : 'draft',
      // Featured fields
      isFeatured: course.isFeatured || false,
      featuredOrder: course.featuredOrder?.toString() || '',
      featuredBadge: course.featuredBadge || '',
      featuredColor: course.featuredColor || 'amber',
      originalPrice: course.originalPrice?.toString() || '',
      features: course.features || [],
    });
    setIsModalOpen(true);
    fetchLessons(course._id);
  };

  const handleNewCourse = () => {
    setSelectedCourse(null);
    setCourseForm({
      title: '',
      slug: '',
      description: '',
      thumbnail: '',
      duration: '',
      price: '',
      currency: 'USD',
      category: '',
      startDate: '',
      startDateTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      accessDurationDays: 0,
      accessExpiresAt: '',
      status: 'draft',
      // Featured fields
      isFeatured: false,
      featuredOrder: '',
      featuredBadge: '',
      featuredColor: 'amber',
      originalPrice: '',
      features: [],
    });
    setNewFeature('');
    setLessons([]);
    setIsModalOpen(true);
  };

  const buildCourseData = () => ({
    ...courseForm,
    price: parseFloat(courseForm.price),
    startDate: courseForm.startDate || undefined,
    startDateTimezone: courseForm.startDate ? courseForm.startDateTimezone : undefined,
    featuredOrder: courseForm.featuredOrder ? parseInt(courseForm.featuredOrder) : undefined,
    originalPrice: courseForm.originalPrice ? parseFloat(courseForm.originalPrice) : undefined,
  });

  const handleSubmitCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (selectedCourse) {
        await adminCoursesService.update(selectedCourse._id, buildCourseData());
      } else {
        await adminCoursesService.create(buildCourseData());
      }
      fetchCourses();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Failed to save course:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Save the course's current expiry config, then backfill it onto existing enrollments.
  const handleApplyExpiry = () => {
    if (!selectedCourse) return;
    setConfirmModal({
      isOpen: true,
      title: t('courses.applyExpiryTitle'),
      message: t('courses.applyExpiryMessage'),
      variant: 'warning',
      onConfirm: async () => {
        try {
          await adminCoursesService.update(selectedCourse._id, buildCourseData());
          const r = await adminCoursesService.applyAccessExpiry(selectedCourse._id);
          if (!r.configured) {
            showError(t('courses.applyExpiryNoConfig'));
          } else {
            success(t('courses.applyExpiryDone', { updated: r.updated, total: r.total }));
          }
          fetchCourses();
        } catch (error) {
          console.error('Failed to apply course expiry:', error);
          showError(t('courses.applyExpiryFailed'));
        } finally {
          setConfirmModal(null);
        }
      },
    });
  };

  const handleDeleteCourse = (id: string) => {
    setConfirmModal({
      isOpen: true,
      title: t('courses.deleteCourse'),
      message: t('courses.deleteMessage'),
      variant: 'danger',
      onConfirm: async () => {
        try {
          await adminCoursesService.delete(id);
          fetchCourses();
        } catch (error) {
          console.error('Failed to delete course:', error);
        }
        setConfirmModal(null);
      },
    });
  };

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setCourseForm({
        ...courseForm,
        features: [...courseForm.features, newFeature.trim()],
      });
      setNewFeature('');
    }
  };

  const handleRemoveFeature = (index: number) => {
    setCourseForm({
      ...courseForm,
      features: courseForm.features.filter((_, i) => i !== index),
    });
  };

  const handleNewLesson = () => {
    setSelectedLesson(null);
    setLessonForm({
      title: '',
      content: '',
      videoUrl: '',
      duration: '',
      orderNum: lessons.length + 1,
    });
    setVideoUrlError('');
    setIsLessonModalOpen(true);
  };

  const handleEditLesson = (lesson: Lesson) => {
    setSelectedLesson(lesson);
    setLessonForm({
      title: lesson.title,
      content: lesson.content,
      videoUrl: lesson.videoUrl || '',
      duration: lesson.duration,
      orderNum: lesson.orderNum,
    });
    setVideoUrlError('');
    setIsLessonModalOpen(true);
  };

  const handleSubmitLesson = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCourse) return;
    setIsSubmitting(true);

    const formData = {
      ...lessonForm,
      videoUrl: lessonForm.videoUrl ? toEmbedUrl(lessonForm.videoUrl) : lessonForm.videoUrl,
    };

    try {
      if (selectedLesson) {
        await adminCoursesService.updateLesson(selectedCourse._id, selectedLesson._id, formData);
      } else {
        await adminCoursesService.createLesson(selectedCourse._id, formData);
      }
      fetchLessons(selectedCourse._id);
      setIsLessonModalOpen(false);
    } catch (error) {
      console.error('Failed to save lesson:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteLesson = (lessonId: string) => {
    if (!selectedCourse) return;
    setConfirmModal({
      isOpen: true,
      title: t('courses.deleteLesson'),
      message: t('courses.deleteLessonMessage'),
      variant: 'danger',
      onConfirm: async () => {
        try {
          await adminCoursesService.deleteLesson(selectedCourse._id, lessonId);
          fetchLessons(selectedCourse._id);
        } catch (error) {
          console.error('Failed to delete lesson:', error);
        }
        setConfirmModal(null);
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('courses.title')}</h1>
          <p className="text-gray-500">{t('courses.subtitle')}</p>
        </div>
        <Button onClick={handleNewCourse}>
          <Plus className="w-5 h-5 mr-2" />
          {t('courses.addCourse')}
        </Button>
      </div>

      {courses.length === 0 ? (
        <Card>
          <CardBody>
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('courses.noCourses')}</h3>
              <p className="text-gray-500 mb-4">{t('courses.noCoursesDescription')}</p>
              <Button onClick={handleNewCourse}>
                <Plus className="w-5 h-5 mr-2" />
                {t('courses.createCourse')}
              </Button>
            </div>
          </CardBody>
        </Card>
      ) : (
        <div className="grid gap-4">
          {courses.map((course) => (
            <Card key={course._id}>
              <CardBody>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 rounded-lg bg-gray-200 flex items-center justify-center overflow-hidden">
                      {course.thumbnail ? (
                        <img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover" />
                      ) : (
                        <BookOpen className="w-8 h-8 text-gray-400" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{course.title}</h3>
                        {course.isFeatured && (
                          <span className="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-300">
                            <Star className="w-3 h-3" />
                            {t('common.featured')}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{course.category} | {course.duration}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          course.status === 'published'
                            ? 'bg-green-50 text-green-700'
                            : 'bg-gray-200 text-gray-600'
                        }`}>
                          {course.status}
                        </span>
                        <span className="text-sm font-medium text-primary-400">{course.currency || 'USD'} {course.price}</span>
                        {course.isFeatured && course.featuredOrder && (
                          <span className="text-xs text-gray-500">{t('partners.orderLabel', { order: course.featuredOrder })}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button variant="secondary" size="sm" onClick={() => handleEditCourse(course)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => handleDeleteCourse(course._id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {/* Course Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedCourse ? t('courses.editCourse') : t('courses.newCourse')}
        size="xl"
      >
        <form onSubmit={handleSubmitCourse} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          <div className="grid sm:grid-cols-2 gap-4">
            <Input
              label={t('courses.titleLabel')}
              value={courseForm.title}
              onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
              required
            />
            <Input
              label={t('courses.slugLabel')}
              value={courseForm.slug}
              onChange={(e) => setCourseForm({ ...courseForm, slug: e.target.value })}
              required
              placeholder={t('courses.slugPlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">{t('courses.descriptionLabel')}</label>
            <Suspense fallback={<div className="border border-gray-200 rounded-lg p-3 text-sm text-gray-400 min-h-[180px]">…</div>}>
              <RichTextEditor
                value={courseForm.description}
                onChange={(html) => setCourseForm({ ...courseForm, description: html })}
              />
            </Suspense>
          </div>

          <div className="grid sm:grid-cols-4 gap-4">
            <Input
              label={t('courses.categoryLabel')}
              value={courseForm.category}
              onChange={(e) => setCourseForm({ ...courseForm, category: e.target.value })}
              required
            />
            <Input
              label={t('courses.durationLabel')}
              value={courseForm.duration}
              onChange={(e) => setCourseForm({ ...courseForm, duration: e.target.value })}
              placeholder={t('courses.durationPlaceholder')}
              required
            />
            <Input
              label={t('courses.priceLabel')}
              type="number"
              step="0.01"
              value={courseForm.price}
              onChange={(e) => setCourseForm({ ...courseForm, price: e.target.value })}
              required
            />
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">{t('courses.currencyLabel')}</label>
              <select
                value={courseForm.currency}
                onChange={(e) => setCourseForm({ ...courseForm, currency: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {SUPPORTED_CURRENCIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">{t('courses.startDateLabel')}</label>
            <div className="flex gap-1">
              <input
                type="date"
                value={courseForm.startDate ? courseForm.startDate.split('T')[0] : ''}
                onChange={(e) => setCourseForm({ ...courseForm, startDate: e.target.value })}
                className="flex-1 min-w-0 px-4 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              {courseForm.startDate && (
                <button
                  type="button"
                  onClick={() => setCourseForm({ ...courseForm, startDate: '' })}
                  className="px-2 py-2 text-gray-400 hover:text-red-500 transition-colors"
                  title="Clear start date"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {courseForm.startDate && (
              <select
                value={courseForm.startDateTimezone}
                onChange={(e) => setCourseForm({ ...courseForm, startDateTimezone: e.target.value })}
                className="w-full mt-1 px-4 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 text-xs"
              >
                {COMMON_TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>{timezoneLabel(tz)}</option>
                ))}
              </select>
            )}
            <p className="text-xs text-gray-500 mt-1">{t('courses.startDateHint')}</p>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <AccessExpiryConfig
              value={{ accessDurationDays: courseForm.accessDurationDays, accessExpiresAt: courseForm.accessExpiresAt }}
              onChange={(next) => setCourseForm({ ...courseForm, ...next })}
            />
            {selectedCourse && (
              <div className="mt-3">
                <Button type="button" variant="outline" size="sm" onClick={handleApplyExpiry}>
                  {t('courses.applyExpiryButton')}
                </Button>
                <p className="text-xs text-gray-500 mt-1">{t('courses.applyExpiryHint')}</p>
              </div>
            )}
          </div>

          <ImageEditor
            label={t('courses.thumbnailLabel')}
            value={courseForm.thumbnail}
            onChange={(url) => setCourseForm({ ...courseForm, thumbnail: url })}
            targetType="course"
            shape="rect"
          />

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">{t('courses.statusLabel')}</label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={courseForm.status === 'draft'}
                  onChange={() => setCourseForm({ ...courseForm, status: 'draft' })}
                  className="mr-2"
                />
                <EyeOff className="w-4 h-4 mr-1 text-gray-500" />
                {t('common.draft')}
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={courseForm.status === 'published'}
                  onChange={() => setCourseForm({ ...courseForm, status: 'published' })}
                  className="mr-2"
                />
                <Eye className="w-4 h-4 mr-1 text-green-500" />
                {t('common.published')}
              </label>
            </div>
          </div>

          {/* Featured Section */}
          <div className="border-t border-gray-200 pt-4 mt-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-gray-900 flex items-center gap-2">
                <Star className="w-4 h-4 text-amber-400" />
                {t('courses.featuredSettings')}
              </h4>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={courseForm.isFeatured}
                  onChange={(e) => setCourseForm({ ...courseForm, isFeatured: e.target.checked })}
                  className="mr-2 w-4 h-4 text-amber-500 bg-white border-gray-300 rounded focus:ring-amber-500"
                />
                <span className="text-sm text-gray-600">{t('courses.featureOnHomepage')}</span>
              </label>
            </div>

            {courseForm.isFeatured && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <div className="grid sm:grid-cols-3 gap-4">
                  <Input
                    label={t('courses.featuredOrderLabel')}
                    type="number"
                    min="1"
                    max="6"
                    value={courseForm.featuredOrder}
                    onChange={(e) => setCourseForm({ ...courseForm, featuredOrder: e.target.value })}
                    placeholder="1"
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">{t('courses.featuredBadgeLabel')}</label>
                    <select
                      value={courseForm.featuredBadge}
                      onChange={(e) => setCourseForm({ ...courseForm, featuredBadge: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">{t('courses.selectBadge')}</option>
                      {BADGE_KEYS.map((key) => (
                        <option key={key} value={t(`courses.badge_${key}`)}>{t(`courses.badge_${key}`)}</option>
                      ))}
                    </select>
                  </div>
                  <Input
                    label={t('courses.originalPriceLabel')}
                    type="number"
                    step="0.01"
                    value={courseForm.originalPrice}
                    onChange={(e) => setCourseForm({ ...courseForm, originalPrice: e.target.value })}
                    placeholder={t('courses.originalPricePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">{t('courses.themeColorLabel')}</label>
                  <div className="flex flex-wrap gap-2">
                    {COLOR_KEYS.map((color) => (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => setCourseForm({ ...courseForm, featuredColor: color.value })}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 transition ${
                          courseForm.featuredColor === color.value
                            ? 'border-primary-600 bg-gray-100'
                            : 'border-transparent bg-white hover:bg-gray-100'
                        }`}
                      >
                        <span className={`w-4 h-4 rounded ${color.className}`} />
                        <span className="text-sm text-gray-600">{t(color.labelKey)}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">{t('courses.featuresLabel')}</label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      placeholder={t('courses.featurePlaceholder')}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddFeature();
                        }
                      }}
                    />
                    <Button type="button" variant="outline" onClick={handleAddFeature}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {courseForm.features.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {courseForm.features.map((feature, index) => (
                        <span
                          key={index}
                          className="flex items-center gap-1 px-3 py-1 bg-gray-200 rounded-full text-sm text-gray-600"
                        >
                          {feature}
                          <button
                            type="button"
                            onClick={() => handleRemoveFeature(index)}
                            className="hover:text-red-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Lessons Section */}
          {selectedCourse && (
            <div className="border-t border-gray-200 pt-4 mt-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-gray-900">{t('courses.lessonsLabel', { count: lessons.length })}</h4>
                <Button type="button" variant="outline" size="sm" onClick={handleNewLesson}>
                  <Plus className="w-4 h-4 mr-1" />
                  {t('courses.addLesson')}
                </Button>
              </div>
              {lessons.length > 0 && (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {lessons.sort((a, b) => a.orderNum - b.orderNum).map((lesson) => (
                    <div key={lesson._id} className="flex items-center justify-between p-3 bg-gray-100 rounded-lg">
                      <div>
                        <span className="text-sm font-medium text-gray-900">{lesson.orderNum}. {lesson.title}</span>
                        <span className="text-xs text-gray-500 ml-2">{lesson.duration}</span>
                      </div>
                      <div className="flex space-x-1">
                        <button type="button" onClick={() => handleEditLesson(lesson)} className="p-1 hover:bg-gray-200 rounded">
                          <Edit className="w-4 h-4 text-gray-500" />
                        </button>
                        <button type="button" onClick={() => handleDeleteLesson(lesson._id)} className="p-1 hover:bg-gray-200 rounded">
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-4 pt-4">
            <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" isLoading={isSubmitting}>
              {selectedCourse ? t('courses.updateCourse') : t('courses.createCourse')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Lesson Modal */}
      <Modal
        isOpen={isLessonModalOpen}
        onClose={() => setIsLessonModalOpen(false)}
        title={selectedLesson ? t('courses.editLesson') : t('courses.newLesson')}
      >
        <form onSubmit={handleSubmitLesson} className="space-y-4">
          <Input
            label={t('courses.lessonTitleLabel')}
            value={lessonForm.title}
            onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })}
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">{t('courses.lessonContentLabel')}</label>
            <textarea
              value={lessonForm.content}
              onChange={(e) => setLessonForm({ ...lessonForm, content: e.target.value })}
              rows={4}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <Input
              label={t('courses.videoUrlLabel')}
              value={lessonForm.videoUrl}
              onChange={(e) => {
                setLessonForm({ ...lessonForm, videoUrl: e.target.value });
                setVideoUrlError('');
              }}
              onBlur={(e) => {
                const url = e.target.value.trim();
                if (url) {
                  const err = validateVideoUrl(url);
                  setVideoUrlError(err || '');
                  if (!err) {
                    setLessonForm({ ...lessonForm, videoUrl: toEmbedUrl(url) });
                  }
                }
              }}
              placeholder={t('courses.videoUrlPlaceholder')}
              error={videoUrlError}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('courses.videoUrlHint')}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label={t('courses.lessonDurationLabel')}
              value={lessonForm.duration}
              onChange={(e) => setLessonForm({ ...lessonForm, duration: e.target.value })}
              placeholder={t('courses.lessonDurationPlaceholder')}
              required
            />
            <Input
              label={t('courses.lessonOrderLabel')}
              type="number"
              value={lessonForm.orderNum}
              onChange={(e) => setLessonForm({ ...lessonForm, orderNum: parseInt(e.target.value) })}
              required
            />
          </div>

          <div className="flex justify-end space-x-4 pt-4">
            <Button type="button" variant="secondary" onClick={() => setIsLessonModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" isLoading={isSubmitting}>
              {selectedLesson ? t('courses.updateLesson') : t('courses.createLesson')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Confirm Modal */}
      {confirmModal && (
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal(null)}
          onConfirm={confirmModal.onConfirm}
          title={confirmModal.title}
          message={confirmModal.message}
          variant={confirmModal.variant}
          confirmText={t('common.delete')}
        />
      )}
    </div>
  );
}
